// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what the browser's video decoder does to a value-encoded
 * release's codes, measured on the release's own calibration strip, and
 * the encoding moved to match (owner, 2026-10-11: "the live website is
 * not fixed" — Marine Vessel Traffic still mostly empty).
 *
 * The stream host writes codes in full range (`signaledRange: "full"`,
 * VP9 tagged `color_range=pc`), but Chrome hands WebGL the frame as if it
 * were limited range, expanding luma to `(code − 16) × 255 / 219`.
 * Measured 2026-10-11 on Marine Vessel Traffic, Global Cloud Cover and
 * ERA5 SST, headless and on the GPU decoder alike: the strip's 16, 32,
 * 48, 128, 224 arrive as 0, 19, 37, 130, 242. So the lowest data code
 * (32, one vessel) lands under the no-data threshold (20) and turns
 * transparent — 60 % of the vessel cells of a frame — and every other
 * value is coloured and read a step high. Other browsers and devices
 * may decode it right; that is why this is measured, not assumed.
 *
 * Every release frame carries a calibration strip (`valueEncoding.
 * calibration`: bands of known codes along one row near the bottom).
 * `calibrateRelease` reads that row out of the playing video the way the
 * globes upload it (its own WebGL2 context, `texImage2D`, `readPixels`,
 * as `glLumaSampler` reads the readout), fits a gain and offset over the
 * bands the decoder did not clip, and, when the decoder moved the codes,
 * returns the encoding in the decoder's space (`inDecodedSpace`): the
 * no-data threshold, the data range and every class code mapped through
 * the same line. Values do not change — a decoded code now decodes to the
 * value its source code had — so the palette, the no-data cut, the
 * readout, pins and the analysis tools all agree with the publisher
 * again, with no change to any shader.
 *
 * The decoder's behaviour is a property of the browser, so the last
 * measurement is kept for the session (`withKnownRange`) and applied when
 * a release is resolved, before its video exists; each load measures
 * again and corrects that guess if the strip disagrees.
 */

import type { ReleaseEncoding } from '../types/release-encoding'
import { logger } from '../utils/logger'

/** How the decoder maps a source code to the code WebGL sees: `gain × code + offset`, clamped to 0..255. */
export interface DecoderRange {
  readonly gain: number
  readonly offset: number
}

/** A source code as the decoder delivers it. Fractional on purpose: the encoding's thresholds sit between decoded codes. */
export function decodedCode(range: DecoderRange, code: number): number {
  return Math.min(255, Math.max(0, range.gain * code + range.offset))
}

/** Within a code of identity everywhere in 0..255: nothing to correct. */
export function isIdentityRange(range: DecoderRange): boolean {
  return Math.abs(range.offset) <= 0.75 && Math.abs(range.gain * 255 + range.offset - 255) <= 0.75
}

/**
 * A line through the strip's bands, from the bands the decoder did not
 * clip (observed strictly inside 2..253). Null when fewer than three are
 * left, or when the bands do not lie on a line within 3 codes (a frame
 * that is not a release frame, or a decoder doing something nonlinear):
 * better to draw as before than to correct by a guess.
 */
export function fitDecoderRange(codes: readonly number[], observed: readonly number[]): DecoderRange | null {
  const pairs: Array<[number, number]> = []
  for (let i = 0; i < Math.min(codes.length, observed.length); i++) {
    const o = observed[i]!
    if (o > 2 && o < 253) pairs.push([codes[i]!, o])
  }
  if (pairs.length < 3) return null
  const n = pairs.length
  const mx = pairs.reduce((s, p) => s + p[0], 0) / n
  const my = pairs.reduce((s, p) => s + p[1], 0) / n
  let sxx = 0, sxy = 0
  for (const [x, y] of pairs) { sxx += (x - mx) ** 2; sxy += (x - mx) * (y - my) }
  if (sxx === 0) return null
  const gain = sxy / sxx
  const offset = my - gain * mx
  if (!(gain > 0.5 && gain < 2)) return null
  for (const [x, y] of pairs) if (Math.abs(gain * x + offset - y) > 3) return null
  return { gain, offset }
}

/** The source encoding behind one already moved into a decoder's space (identity for a source one). */
const sourceOf = new WeakMap<ReleaseEncoding, ReleaseEncoding>()
/** The range an encoding was moved by. */
const rangeOf = new WeakMap<ReleaseEncoding, DecoderRange>()

/** The encoding as the publisher wrote it. */
export function sourceEncoding(enc: ReleaseEncoding): ReleaseEncoding {
  return sourceOf.get(enc) ?? enc
}

/**
 * One step of the data in value units, `(vmax − vmin)` over the codes the
 * publisher spread it across. Taken from the source encoding: a decoder
 * that spreads 203 codes over 236 does not make the data any finer.
 */
export function sourceCodeStep(enc: ReleaseEncoding): number {
  const source = sourceEncoding(enc)
  return (source.vmax - source.vmin) / (source.dataMaxCode - source.dataMinCode)
}

/**
 * The encoding in the decoder's code space: the same values, at the codes
 * the decoder delivers them. The no-data threshold, `dataMinCode`,
 * `dataMaxCode` and every class and auxiliary class code go through
 * `decodedCode`; everything else (values, palette, regions) is unchanged.
 */
export function inDecodedSpace(enc: ReleaseEncoding, range: DecoderRange): ReleaseEncoding {
  const source = sourceEncoding(enc)
  if (isIdentityRange(range)) return source
  const at = (code: number): number => decodedCode(range, code)
  const moved: ReleaseEncoding = {
    ...source,
    nodataThresholdCode: at(source.nodataThresholdCode),
    dataMinCode: at(source.dataMinCode),
    dataMaxCode: at(source.dataMaxCode),
    classes: source.classes.map((c) => ({ ...c, code: at(c.code) })),
    ...(source.auxiliary
      ? { auxiliary: source.auxiliary.map((a) => ({ ...a, classes: a.classes.map((c) => ({ ...c, code: at(c.code) })) })) }
      : {}),
    // Readings snap back to the source lattice (types/release-encoding.ts `sourceCodeAt`).
    decodedFrom: { source, gain: range.gain, offset: range.offset },
  }
  // A clipped top (235 → 255 under limited-range expansion) must not collapse the palette.
  if (!(moved.dataMaxCode > moved.dataMinCode)) return source
  sourceOf.set(moved, source)
  rangeOf.set(moved, range)
  return moved
}

/** The last range measured in this session (null: none measured, or the decoder was exact). */
let known: DecoderRange | null = null

/** For tests. */
export function resetKnownRange(): void { known = null }

/** A just-resolved release in the space this browser's decoder was last measured in; unchanged before any measurement. */
export function withKnownRange(enc: ReleaseEncoding): ReleaseEncoding {
  return known ? inDecodedSpace(enc, known) : sourceEncoding(enc)
}

/** Resolves true once the video has a frame to read (readyState ≥ 2), false after `timeoutMs`. */
export function videoHasFrame(video: HTMLVideoElement, timeoutMs: number): Promise<boolean> {
  if (video.readyState >= 2) return Promise.resolve(true)
  return new Promise((resolve) => {
    const done = (ok: boolean): void => {
      clearTimeout(timer)
      video.removeEventListener('loadeddata', onData)
      resolve(ok)
    }
    const onData = (): void => done(true)
    const timer = setTimeout(() => done(video.readyState >= 2), timeoutMs)
    video.addEventListener('loadeddata', onData)
  })
}

/** One WebGL2 context for every measurement, built on first use. */
let reader: { gl: WebGL2RenderingContext; tex: WebGLTexture; fb: WebGLFramebuffer } | null = null

/** The strip's row of the current frame, red channel, as the globes would upload it; null when it cannot be read. */
function readRow(video: HTMLVideoElement, row: number): Uint8Array | null {
  const w = video.videoWidth
  const h = video.videoHeight
  if (!w || !h || row < 0 || row >= h || video.readyState < 2) return null
  try {
    if (!reader) {
      const gl = document.createElement('canvas').getContext('webgl2')
      const tex = gl?.createTexture()
      const fb = gl?.createFramebuffer()
      if (!gl || !tex || !fb) return null
      reader = { gl, tex, fb }
    }
    const { gl, tex, fb } = reader
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video)
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0)
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) return null
    const px = new Uint8Array(w * 4)
    // Texture row 0 is the frame's top row (no flip), so `row` reads as-is.
    gl.readPixels(0, row, w, 1, gl.RGBA, gl.UNSIGNED_BYTE, px)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    // Release the frame-sized texture between measurements.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
    const red = new Uint8Array(w)
    for (let x = 0; x < w; x++) red[x] = px[x * 4]!
    return red
  } catch (err) {
    logger.warn('[decoderRange] could not read the calibration strip:', err)
    return null
  }
}

/** Each band's code, as the median of its middle half (clear of the edges, where the codec blends neighbours). */
export function bandCodes(row: Uint8Array, bands: number): number[] {
  const out: number[] = []
  const w = row.length / bands
  for (let b = 0; b < bands; b++) {
    const from = Math.floor(b * w + w / 4)
    const to = Math.max(from + 1, Math.floor(b * w + (3 * w) / 4))
    const vals = Array.from(row.subarray(from, to)).sort((x, y) => x - y)
    out.push(vals[Math.floor(vals.length / 2)]!)
  }
  return out
}

/**
 * The release's encoding in the space this video's decoder delivers,
 * measured on the current frame's calibration strip. Returns `enc`
 * itself when nothing changes (no strip, unreadable, or already in the
 * measured space), so a caller can compare by identity.
 */
export function calibrateRelease(video: HTMLVideoElement, enc: ReleaseEncoding): ReleaseEncoding {
  const source = sourceEncoding(enc)
  const cal = source.calibration
  if (!cal) return enc
  const row = readRow(video, cal.row)
  if (!row) return enc
  const observed = bandCodes(row, cal.codes.length)
  const range = fitDecoderRange(cal.codes, observed)
  if (!range) {
    logger.warn('[decoderRange] calibration strip does not fit a line; drawing as published', { codes: cal.codes, observed })
    return enc
  }
  known = isIdentityRange(range) ? null : range
  const was = rangeOf.get(enc)
  if (isIdentityRange(range)) return enc === source ? enc : source
  if (was && Math.abs(was.gain - range.gain) < 0.002 && Math.abs(was.offset - range.offset) < 0.25) return enc
  logger.info('[decoderRange] decoder moves release codes; encoding moved to match', { gain: range.gain, offset: range.offset })
  return inDecodedSpace(source, range)
}
